import { useSyncExternalStore } from 'react';
import { toastStore, dismissToast, type Toast } from '../lib/toast';
import { TX_ETHERSCAN } from '../lib/chain';

/**
 * Fixed-position toast stack. Mount once at the app root (App.tsx).
 * Reads from toastStore (no provider needed).
 */
export default function Toasts() {
  const { toasts } = useSyncExternalStore(toastStore.subscribe, toastStore.get);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        maxWidth: 380,
      }}
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastCard({ toast }: { toast: Toast }) {
  const accent =
    toast.kind === 'success' ? 'var(--green)' :
    toast.kind === 'error'   ? 'var(--red)' :
    toast.kind === 'pending' ? 'var(--amber)' :
                                'var(--ctx)';
  const icon =
    toast.kind === 'success' ? '✓' :
    toast.kind === 'error'   ? '✗' :
    toast.kind === 'pending' ? '…' :
                                'ℹ';

  return (
    <div
      className="panel"
      style={{
        pointerEvents: 'auto',
        borderLeft: `3px solid ${accent}`,
        padding: '10px 12px',
        background: 'var(--panel)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
        fontSize: 12,
        lineHeight: 1.45,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ color: accent, fontFamily: 'var(--mono)', fontSize: 14, minWidth: 14, textAlign: 'center' }}>
          {icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{toast.title}</div>
          {toast.message && (
            <div style={{ color: 'var(--dim)', marginTop: 2, wordBreak: 'break-word' }}>
              {toast.message}
            </div>
          )}
          {toast.txHash && (
            <a
              href={TX_ETHERSCAN(toast.txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="mono"
              style={{ color: accent, fontSize: 10, letterSpacing: 0.5, marginTop: 4, display: 'inline-block' }}
            >
              {toast.txHash.slice(0, 10)}…{toast.txHash.slice(-6)} ↗
            </a>
          )}
        </div>
        <button
          onClick={() => dismissToast(toast.id)}
          aria-label="dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--dim)',
            cursor: 'pointer',
            fontSize: 14,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
